import { supabase } from "./supabase";
import type { Pergunta } from "./irproposta";

// O lado PÚBLICO do IR Proposta: o cliente do aluno respondendo o formulário.
//
// ---------------------------------------------------------------------------
// Aqui não existe sessão, e isso muda o caminho de tudo
// ---------------------------------------------------------------------------
// Quem abre esta tela não tem conta na CF e não vai ter. Então:
//
//   * o QUESTIONÁRIO vem pela Data API com a chave `anon`, direto de
//     `irp_questionario()` — a função tem grant para `anon` e a policy de
//     `irp_perguntas` libera leitura do catálogo. Não há nada de ninguém ali;
//   * a RESPOSTA vai pela Edge Function `responder-orcamento`, que é a única com
//     permissão de chamar `irp_registrar_resposta`. Escrita não passa pela Data
//     API: `anon` não tem grant nem na tabela nem na função, e o teste 38 prova.
//
// A chamada de validação e a de gravação usam `fetch` direto em vez de
// `functions.invoke`: `invoke` sempre faz POST e injeta o Authorization da sessão,
// e aqui um dos dois verbos é GET e sessão não existe. A chave `anon` vai à mão,
// que é o que o gateway espera de uma function com `verify_jwt = false`.

const BASE = import.meta.env.VITE_SUPABASE_URL as string;
const ANON = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
const FUNCAO = `${BASE}/functions/v1/responder-orcamento`;

export type EstadoDoLink =
  | { valido: true; anoCalendario: number; contador: string }
  | { valido: false; motivo: "link-invalido" | "sem-acesso" | "temporada-fechada" | "falha" };

/** Nunca lança: o cliente não pode ver stack trace de ninguém. */
export async function validarLink(token: string): Promise<EstadoDoLink> {
  if (!/^[0-9a-f]{32}$/.test(token)) return { valido: false, motivo: "link-invalido" };

  try {
    const r = await fetch(`${FUNCAO}?token=${encodeURIComponent(token)}`, {
      headers: { apikey: ANON, authorization: `Bearer ${ANON}` },
    });
    const corpo = await r.json().catch(() => null);

    if (r.ok && typeof corpo?.ano_calendario === "number") {
      return {
        valido: true,
        anoCalendario: corpo.ano_calendario,
        contador: String(corpo.contador ?? ""),
      };
    }
    const motivo = String(corpo?.motivo ?? "falha");
    return {
      valido: false,
      motivo:
        motivo === "link-invalido" || motivo === "sem-acesso" || motivo === "temporada-fechada"
          ? motivo
          : "falha",
    };
  } catch {
    return { valido: false, motivo: "falha" };
  }
}

export async function carregarQuestionarioPublico(): Promise<Pergunta[]> {
  const { data, error } = await supabase.rpc("irp_questionario");
  if (error || !data) return [];
  return data as Pergunta[];
}

/**
 * O que o cliente respondeu de uma pergunta.
 *
 * `valor` é o que o CAMPO MOSTRA ("R$ 1.234,56") e `valorLimpo` é o que o BANCO
 * RECEBE ("1234.56"). Os dois existem porque a máscara é apresentação: mandar o
 * texto com ponto de milhar para uma coluna `numeric` é o mesmo defeito que o campo
 * sem máscara tinha — o valor virava nulo em silêncio, e o aluno via o campo vazio
 * sem saber que o cliente havia respondido.
 *
 * O banco tolera as duas formas desde a 000069 (`numero_pt_br`), mas mandar limpo é
 * o certo: a tolerância existe para quem chega por outro caminho, não para o front
 * se apoiar nela.
 */
export type RespostaEnviada = {
  sim?: boolean;
  escolha?: string;
  valor?: string;
  valorLimpo?: string;
};

export type EnvioDoOrcamento = {
  nome: string;
  email: string;
  telefone: string;
  cpf: string;
  cep: string;
  endereco: string;
  complemento: string;
  bairro: string;
  cidade: string;
  estado: string;
  observacoes: string;
  respostas: Record<string, RespostaEnviada>;
};

export type ResultadoEnvio =
  | { ok: true }
  | { ok: false; motivo: string; mensagem: string };

/**
 * O que o cliente lê quando a resposta não entra.
 *
 * Cada motivo tem texto próprio porque as ações são diferentes, e o público aqui é
 * o menos capaz de adivinhar: ele não conhece a CF, não sabe o que é "temporada" e
 * não tem suporte para acionar. Ou a frase diz o que fazer, ou ele desiste — e o
 * lead que desiste é a razão de a ferramenta existir.
 */
const MENSAGENS: Record<string, string> = {
  "link-invalido":
    "Este link não está mais ativo. Peça ao seu contador um link novo para preencher o orçamento.",
  "sem-acesso":
    "Este formulário está indisponível no momento. Fale com o seu contador para receber um link atualizado.",
  "temporada-fechada":
    "O período de orçamentos do Imposto de Renda está fechado agora. Fale com o seu contador.",
  "nome-obrigatorio": "Escreva o seu nome completo.",
  "contato-obrigatorio": "Informe pelo menos um e-mail ou um telefone para o contador falar com você.",
  "cpf-invalido": "O CPF informado não é válido. Confira os números.",
  "estado-invalido": "Use a sigla do estado, com duas letras (por exemplo, SP).",
  "muitas-respostas":
    "Recebemos muitas respostas por este link nos últimos minutos. Tente de novo em uma hora.",
};

export function mensagemDoEnvio(motivo: string): string {
  return (
    MENSAGENS[motivo] ??
    "Não conseguimos registrar a sua resposta agora. Tente de novo em alguns instantes."
  );
}

/**
 * Troca o texto mascarado pelo valor limpo antes de sair do navegador.
 *
 * Aqui, e não na tela: este arquivo é a fronteira com o servidor, e é o único lugar
 * por onde toda resposta passa. Fazer na tela deixaria o próximo campo de valor
 * (quando a calculadora chegar) livre para esquecer.
 */
function paraOServidor(
  respostas: Record<string, RespostaEnviada>,
): Record<string, { sim?: boolean; escolha?: string; valor?: string }> {
  const saida: Record<string, { sim?: boolean; escolha?: string; valor?: string }> = {};
  for (const [codigo, r] of Object.entries(respostas)) {
    saida[codigo] = {
      sim: r.sim,
      escolha: r.escolha,
      // `valorLimpo` pode ser string vazia (campo apagado), e aí o certo é mandar
      // vazio — `??` preserva isso, `||` cairia no texto mascarado.
      valor: r.valorLimpo ?? r.valor,
    };
  }
  return saida;
}

export async function enviarOrcamento(
  token: string,
  dados: EnvioDoOrcamento,
): Promise<ResultadoEnvio> {
  try {
    const r = await fetch(FUNCAO, {
      method: "POST",
      headers: {
        apikey: ANON,
        authorization: `Bearer ${ANON}`,
        "content-type": "application/json",
      },
      body: JSON.stringify({ token, ...dados, respostas: paraOServidor(dados.respostas) }),
    });

    if (r.ok) return { ok: true };

    const corpo = await r.json().catch(() => null);
    const motivo = String(corpo?.motivo ?? "falha");
    return { ok: false, motivo, mensagem: mensagemDoEnvio(motivo) };
  } catch {
    return { ok: false, motivo: "falha", mensagem: mensagemDoEnvio("falha") };
  }
}
